import React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { DateTime } from 'acscp/store/dateTime';

export const yearDateMaskSymbol = '●●●●'; // Used in IWAS as well. Do not change here!
export const yearDateMaskValue = '2000'; // Should be a 4-digit leap-year
const dayDateMaskSymbol = '●●'; // Used in IWAS as well. Do not change here!
const dayDateMaskValue = '28'; // Should be a 2-digit day of the month (<=28) higher than the number of months in a year (>12)

export enum DateStyle {
    Medium,
    Long,
    ExtraLong,
}

export interface FormattedDateProps {
    date: DateTime;
    style?: DateStyle;
    maskYear?: boolean;
}
type FormattedDateWithIntlOwnProps = FormattedDateProps & InjectedIntlProps;

interface FormattedDateWithMaskedDayProps {
    maskedApiDate: string;
    style?: DateStyle;
}
type FormattedDateWithMaskedDayWithIntlOwnProps = FormattedDateWithMaskedDayProps & InjectedIntlProps;

interface DateTimeFormatOptions {
    year: 'numeric' | '2-digit';
    month: 'numeric' | '2-digit' | 'narrow' | 'short' | 'long';
    day: 'numeric' | '2-digit' | undefined;
    weekday: 'narrow' | 'short' | 'long' | undefined;
}

function getDateFormat(style: DateStyle, includeDate: boolean): DateTimeFormatOptions {
    switch (style) {
        case DateStyle.Medium:
            return includeDate
                ? {weekday: undefined, day: '2-digit', month: 'short', year: 'numeric'}
                : {weekday: undefined, day: undefined, month: 'short', year: 'numeric'};
        case DateStyle.Long:
            return includeDate
                ? {weekday: 'long', day: '2-digit', month: 'short', year: 'numeric'}
                : {weekday: undefined, day: undefined, month: 'short', year: 'numeric'};
        case DateStyle.ExtraLong:
            return includeDate
                ? {weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'}
                : {weekday: undefined, day: undefined, month: 'long', year: 'numeric'};
    }
}

export const formatDate = (intl: ReactIntl.InjectedIntl, date: DateTime, options?: ReactIntl.FormattedDate.PropsBase): string => {
    let formattedDate = intl.formatDate(date.getDate(), options);

    if (intl.locale === 'th' || intl.locale === 'th-TH') {
        // Quotes in Thai should use the Gregorian calendar
        // We use a convenient property of the Buddhist calendar to work around (our current version of) react-intl
        const gregorianYear = date.getDate().getUTCFullYear();
        formattedDate = formattedDate.replace(/\d{4,}/, gregorianYear.toString());
    }

    return formattedDate;
};

const FormattedDateWithIntl: React.SFC<FormattedDateWithIntlOwnProps> =
({ date, style = DateStyle.Medium, maskYear = false, intl }) => {
    // Return null instead of "Invalid Date"
    if (!date.isValid()) {
        return null;
    }

    const formatOptions = getDateFormat(style, date.hasDate());
    const formattedDate = formatDate(intl, date, formatOptions);
    const yearMaskedDate = formattedDate.replace(/\d{4,}/, yearDateMaskSymbol);

    return maskYear
        ? (<span>{yearMaskedDate}</span>)
        : (<span>{formattedDate}</span>);
};

const FormattedDate = injectIntl(FormattedDateWithIntl);

const FormattedDateWithMaskedDayWithIntl: React.SFC<FormattedDateWithMaskedDayWithIntlOwnProps> =
({ maskedApiDate, style = DateStyle.Medium, intl }) => {
    const dateWithFakeDay = new DateTime(maskedApiDate.replace(dayDateMaskSymbol, dayDateMaskValue));

    // Return null instead of "Invalid Date"
    if (!dateWithFakeDay.isValid()) {
        return null;
    }

    const formatOptions = getDateFormat(style, dateWithFakeDay.hasDate());
    const formattedDate = formatDate(intl, dateWithFakeDay, formatOptions);
    let dayMaskedDate = formattedDate;
    // Re-replace the dayDateMaskValue with the dayDateMaskSymbol
    // Check the matches are not preceded or followed by another number to ensure that it is not part of the 4 digit year e.g. 2028
    // Negative look-ahead or look-behind regexes are not able to be used as many browsers do not support this, so it must be done in JS
    for (let i = 0; i <= dayMaskedDate.length - dayDateMaskValue.length; i++) {
        const subString = dayMaskedDate.substring(i, i + dayDateMaskValue.length);
        const isPrecededByANumber = i !== 0 && dayMaskedDate[i - 1].match(/\d/);
        const isFollowedByANumber = i < dayMaskedDate.length - dayDateMaskValue.length
            && dayMaskedDate[i + dayDateMaskValue.length].match(/\d/);

        if (subString === dayDateMaskValue && !isPrecededByANumber && !isFollowedByANumber) {
            dayMaskedDate = dayMaskedDate.substr(0, i) + dayDateMaskSymbol + dayMaskedDate.substr(i + dayDateMaskValue.length);
        }
    }
    return (<span>{dayMaskedDate}</span>);
};

const FormattedDateWithMaskedDay = injectIntl(FormattedDateWithMaskedDayWithIntl);

export { FormattedDate, FormattedDateWithMaskedDay, getDateFormat };
